import Axios, { AxiosRequestConfig } from 'axios';
import designRendersService, {
  DesignPreviews,
  PreviewTypeAvailability,
  SceneCategories,
} from 'src/api/designRendersService';
import { DesignData } from 'src/store/design/types';

enum ProductPreviewType {
  SCENE = 'scene',
  IMAGE = 'image',
  AR_VIEWER = 'ar-viewer',
}

/**
 * Design renders loader that uses the dashboard GraphQL API
 * Replaces the legacy gelato-api-design-renders-loader
 */
class DesignRendersLoader {
  /**
   * Load design renders for a given design structure
   * @param designData - The design structure to render
   * @param sceneCategories - Scene categories to use ('editor' or 'all')
   * @param premiumSceneSize - Premium scene size (default 800)
   * @returns Promise with design previews or null
   */
  // eslint-disable-next-line class-methods-use-this
  async loadDesignRenders(
    designData: DesignData,
    sceneCategories: SceneCategories = 'editor',
    premiumSceneSize = 800,
  ): Promise<DesignPreviews | null> {
    try {
      // Call GraphQL API
      const renders = await designRendersService.getRenders(
        designData,
        false, // removeSamples
        sceneCategories,
        premiumSceneSize,
      );

      return renders;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading design renders:', error);
      return null;
    }
  }

  /**
   * Get available preview types for a product
   * @param productUid - The product UID
   * @returns Promise with available preview types
   */
  // eslint-disable-next-line class-methods-use-this
  async getAvailablePreviews(productUid: string): Promise<PreviewTypeAvailability | null> {
    try {
      const params = new URLSearchParams({ product_uid: productUid });
      const url = `${window.integrationLayer.preFlightApiBaseUrl}/product/preview/types?${params}`;

      const config: AxiosRequestConfig = {
        url,
        method: 'GET',
      };

      const response = await Axios(config);
      const availablePreviews = response.data as string[];

      if (!availablePreviews) {
        return null;
      }

      return {
        flat:
          availablePreviews.includes(ProductPreviewType.SCENE)
          || availablePreviews.includes(ProductPreviewType.IMAGE),
        the3d: availablePreviews.includes(ProductPreviewType.AR_VIEWER),
        image: availablePreviews.includes(ProductPreviewType.IMAGE),
        scene: availablePreviews.includes(ProductPreviewType.SCENE),
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error getting available previews:', error);
      return null;
    }
  }
}

const designRendersLoader = new DesignRendersLoader();

export default designRendersLoader;
